// conciergeApi.js

import { apiClient } from "./apiClient";

// Resolve a hinted staff id from globals/localStorage
function __resolveStaffIdHint() {
  try {
    if (typeof window !== 'undefined') {
      const w = window;
      // Prefer explicit staff hints
      const fromWin = w.__STAFF_ID__ || (w.__STAFF__ && (w.__STAFF__._id || w.__STAFF__.id));
      if (fromWin) return String(fromWin);
      // Fall back to your app's user object
      if (w.__EF_USER && (w.__EF_USER._id || w.__EF_USER.id)) {
        return String(w.__EF_USER._id || w.__EF_USER.id);
      }
    }
    if (typeof localStorage !== 'undefined') {
      const fromLs = localStorage.getItem('staffId') || localStorage.getItem('userId');
      if (fromLs) return String(fromLs);
    }
  } catch {}
  return null;
}

// Resolve a hinted staff display name from globals/localStorage
function __resolveStaffNameHint() {
  try {
    if (typeof window !== 'undefined') {
      const w = window;
      const fromWin = (w.__STAFF__ && (w.__STAFF__.displayName || w.__STAFF__.username || w.__STAFF__.name))
        || w.__STAFF_NAME__
        || (w.__EF_USER && (w.__EF_USER.username || w.__EF_USER.displayName || w.__EF_USER.name))
        || null;
      if (fromWin) return String(fromWin);
    }
    if (typeof localStorage !== 'undefined') {
      const fromLs = localStorage.getItem('staffName') || localStorage.getItem('username') || localStorage.getItem('name');
      if (fromLs) return String(fromLs);
    }
  } catch {}
  return null;
}

// Attach x-staff-id on all apiClient requests (belt-and-suspenders)
try {
  if (apiClient && apiClient.interceptors && apiClient.interceptors.request && typeof apiClient.interceptors.request.use === 'function') {
    apiClient.interceptors.request.use((config) => {
      try {
        const id = __resolveStaffIdHint();
        if (id) {
          config.headers = config.headers || {};
          if (!config.headers['x-staff-id']) config.headers['x-staff-id'] = id;
        }
      } catch {}
      return config;
    });
  }
} catch {}

// ---------------- Hotel-side (no userUid) ----------------
// List threads for a property
export async function getThreads({ propertyId, page = 1, limit = 50, q }) {
  if (!propertyId) throw new Error("propertyId is required");
  console.debug("[conciergeApi] getThreads(hotel)", { propertyId, page, limit, q });
  const res = await apiClient.get("/api/concierge-messages/threads", {
    query: { propertyId, page, limit, q },
  });
  return Array.isArray(res?.threads) ? res.threads : [];
}

// ---------------- Guest-side (requires userUid) ----------------
// Ensure/get a single thread for a specific guest
export async function getOrEnsureThread({ propertyId, userUid, guestEmail }) {
  if (!propertyId) throw new Error("propertyId is required");
  if (!userUid) throw new Error("userUid is required");
  console.debug("[conciergeApi] getOrEnsureThread", { propertyId, userUid, guestEmail });
  return apiClient.get("/api/concierge-messages/thread", {
    query: { propertyId, userUid, guestEmail },
  });
}

// Read full message history for a guest thread (paginated)
export async function getHistory({ propertyId, userUid, page = 1, limit = 50 }) {
  if (!propertyId) throw new Error("propertyId is required");
  if (!userUid) throw new Error("userUid is required");
  console.debug("[conciergeApi] getHistory", { propertyId, userUid, page, limit });
  const res = await apiClient.get("/api/concierge-messages/history", {
    query: { propertyId, userUid, page, limit },
  });
  // Backend returns { success, messages, total, page, limit }
  return Array.isArray(res?.messages) ? res.messages : [];
}

// Send a message in a guest threads
export async function sendMessage({ propertyId, userUid, sender, content, tempId, attachments, replyTo, replySnapshot }) {
  if (!propertyId) throw new Error("propertyId is required");
  if (!userUid) throw new Error("userUid is required");
  if (!sender) throw new Error("sender is required");

  const hasAtt = Array.isArray(attachments) && attachments.length > 0;
  const finalContent = (content && String(content).trim()) || (hasAtt ? '(attachment)' : '');

  const body = { propertyId, userUid, sender, content: finalContent };
  if (tempId) body.tempId = tempId;
  if (hasAtt) body.attachments = attachments;
  if (replyTo) body.replyTo = replyTo;
  if (replySnapshot) body.replySnapshot = replySnapshot; // { text, sender, createdAt }
  console.debug("[conciergeApi] sendMessage", { ...body, attCount: hasAtt ? attachments.length : 0, hasReply: !!replyTo || !!replySnapshot });
  return apiClient.post("/api/concierge-messages/send", body);
}

// Convenience: send as staff (concierge side)
export async function sendStaffMessage({ propertyId, userUid, content, tempId, attachments, replyTo, replySnapshot }) {
  const idHint = __resolveStaffIdHint();
  const nameHint = __resolveStaffNameHint();
  const sender = { role: 'staff' };
  if (idHint) sender.id = String(idHint);
  if (nameHint) sender.displayName = String(nameHint);
  return sendMessage({
    propertyId,
    userUid,
    sender,
    content,
    tempId,
    attachments,
    replyTo,
    replySnapshot,
  });
}

// Convenience: send as guest (guest side)
export async function sendGuestMessage({ propertyId, userUid, content, tempId, attachments, replyTo, replySnapshot }) {
  return sendMessage({
    propertyId,
    userUid,
    sender: { id: userUid, role: 'guest' },
    content,
    tempId,
    attachments,
    replyTo,
    replySnapshot,
  });
}

// Legacy wrapper: prefer markSeen (role-aware, idempotent)
export async function seenMessage({ propertyId, userUid, messageId }) {
  return markSeen({ propertyId, userUid, messageId, actor: { role: 'staff' } });
}

// Role-aware read receipt (append-only, idempotent). Actor defaults to 'staff' on backoffice.
export async function markSeen({ propertyId, userUid, messageId, actor }) {
  if (!propertyId) throw new Error("propertyId is required");
  if (!userUid) throw new Error("userUid is required");
  if (!messageId) throw new Error("messageId is required");
  const role = actor && (actor.role === 'guest' || actor.role === 'staff') ? actor.role : 'staff';
  const cleanActor = { ...(actor || { role: 'staff' }) };
  cleanActor.role = role;
  if (cleanActor.id != null) cleanActor.id = String(cleanActor.id);
  console.debug("[conciergeApi] markSeen", { propertyId, userUid, messageId, actor: cleanActor });
  return apiClient.post("/api/concierge-messages/seen", { propertyId, userUid, messageId, actor: cleanActor });
}

// Mark all staff-unread messages read for a thread (hotel-side)
export async function markRead({ propertyId, userUid, actor }) {
  if (!propertyId) throw new Error("propertyId is required");
  if (!userUid) throw new Error("userUid is required");

  // Prefer caller-provided actor; otherwise derive from hints
  let derivedActor = actor && typeof actor === 'object' ? { ...actor } : { role: 'staff' };
  if (derivedActor.id == null) {
    const idHint = __resolveStaffIdHint();
    if (idHint) derivedActor.id = String(idHint);
  } else {
    derivedActor.id = String(derivedActor.id);
  }
  if (!derivedActor.role || (derivedActor.role !== 'staff' && derivedActor.role !== 'guest')) {
    derivedActor.role = 'staff';
  }

  console.debug("[conciergeApi] markRead", { propertyId, userUid, actor: derivedActor });

  const headers = {};
  if (derivedActor.id) headers['x-staff-id'] = String(derivedActor.id);

  return apiClient.put(
    "/api/concierge-messages/mark-read",
    { propertyId, userUid, actor: derivedActor },
    { headers }
  );
}

// Utility: build a deterministic room key used by sockets (kept here for convenience)
export function buildRoomKey({ propertyId, userUid }) {
  if (!propertyId) throw new Error("propertyId is required");
  if (!userUid) throw new Error("userUid is required");
  return `concierge:${propertyId}:${userUid}`;
}

export async function getGuestProfile({ propertyId, userUid }) {
  if (!propertyId) throw new Error("propertyId is required");
  if (!userUid) throw new Error("userUid is required");
  console.debug("[conciergeApi] getGuestProfile", { propertyId, userUid });
  try {
    const res = await apiClient.get("/api/concierge-messages/guest", { query: { propertyId, userUid } });
    return res?.guest || null;
  } catch (e) {
    console.warn("[conciergeApi] getGuestProfile apiClient.get failed, falling back to fetch", e);
    const url = `/api/concierge-messages/guest?propertyId=${encodeURIComponent(propertyId)}&userUid=${encodeURIComponent(userUid)}`;
    const r = await fetch(url, { credentials: 'include', headers: (() => { const h = {}; const id = __resolveStaffIdHint(); if (id) h['x-staff-id'] = id; return h; })() });
    if (!r.ok) throw new Error(`Failed to fetch guest profile (${r.status})`);
    const data = await r.json();
    return data?.guest || null;
  }
}

export async function getCheckinLogs({ propertyId, userUid, limit = 25 }) {
  if (!propertyId) throw new Error("propertyId is required");
  if (!userUid) throw new Error("userUid is required");
  console.debug("[conciergeApi] getCheckinLogs", { propertyId, userUid, limit });
  try {
    const res = await apiClient.get("/api/checkin/logs", { query: { propertyId, userUid, limit } });
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.logs)) return res.logs;
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.items)) return res.items;
    return [];
  } catch (e) {
    console.warn("[conciergeApi] getCheckinLogs apiClient.get failed, falling back to fetch", e);
    const url = `/api/checkin/logs?propertyId=${encodeURIComponent(propertyId)}&userUid=${encodeURIComponent(userUid)}&limit=${limit}`;
    const r = await fetch(url, { credentials: 'include', headers: (() => { const h = {}; const id = __resolveStaffIdHint(); if (id) h['x-staff-id'] = id; return h; })() });
    if (!r.ok) throw new Error(`Failed to fetch check-in logs (${r.status})`);
    const data = await r.json();
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.logs)) return data.logs;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.items)) return data.items;
    return [];
  }
}
export async function uploadAttachment({ propertyId, userUid, file }) {
  if (!propertyId) throw new Error('propertyId is required');
  if (!userUid) throw new Error('userUid is required');
  if (!file) throw new Error('file is required');

  const form = new FormData();
  form.append('propertyId', propertyId);
  form.append('userUid', userUid);
  form.append('file', file);

  // 1) Prefer apiClient with a longer timeout for uploads (compression + network can exceed 15s)
  try {
    const res = await apiClient.post('/api/concierge-messages/upload', form, { timeoutMs: 60000 });
    if (!res?.success) {
      const err = new Error(res?.message || 'Upload failed');
      err.code = res?.code; throw err;
    }
    return res.attachment;
  } catch (e) {
    console.warn('[conciergeApi] uploadAttachment apiClient.post failed, falling back to fetch', e);

    // 2) Build absolute backend URL so we don't hit the frontend dev origin (7072)
    const envBase = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_URL)
      || (typeof process !== 'undefined' && process.env && (process.env.VITE_API_URL || process.env.REACT_APP_API_URL))
      || (apiClient && (apiClient.baseUrl || apiClient.defaults?.baseURL))
      || '';
    const base = String(envBase || '').replace(/\/$/, '');
    const url = base ? `${base}/api/concierge-messages/upload` : '/api/concierge-messages/upload';

  // 3) Fetch fallback with its own timeout (60s)
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), 60000);
  const r = await fetch(url, { method: 'POST', body: form, credentials: 'include', signal: ctrl.signal, headers: (() => { const h = {}; const id = __resolveStaffIdHint(); if (id) h['x-staff-id'] = id; return h; })() });
  clearTimeout(t);

    if (r.status === 413) {
      let msg = 'File too large.';
      try { const j = await r.json(); msg = j?.message || msg; } catch {}
      const err = new Error(msg); err.code = 'FILE_TOO_LARGE'; err.status = 413; throw err;
    }

    if (!r.ok) throw new Error(`Upload failed (${r.status})`);
    const data = await r.json();
    if (!data?.success) {
      const err = new Error(data?.message || 'Upload failed'); err.code = data?.code; throw err;
    }
    return data.attachment;
  }
}

// Delete a single attachment by its Cloudinary publicId
export async function deleteAttachment({ propertyId, userUid, messageId, publicId }) {
  if (!propertyId) throw new Error("propertyId is required");
  if (!userUid) throw new Error("userUid is required");
  if (!messageId) throw new Error("messageId is required");
  if (!publicId) throw new Error("publicId is required");
  console.debug("[conciergeApi] deleteAttachment API call", {
    ts: new Date().toISOString(),
    propertyId,
    userUid,
    messageId,
    publicId
  });
  return apiClient.post("/api/concierge-messages/delete-attachment", {
    propertyId, userUid, messageId, publicId
  });
}

// Convenience: delete by publicId only (useful when only a tempId exists)
export async function deleteAttachmentByPublicId({ propertyId, userUid, publicId }) {
  if (!propertyId) throw new Error("propertyId is required");
  if (!userUid) throw new Error("userUid is required");
  if (!publicId) throw new Error("publicId is required");
  const body = { propertyId, userUid, publicId };
  console.debug("[conciergeApi] deleteAttachmentByPublicId API call", {
    ts: new Date().toISOString(),
    ...body
  });
  return apiClient.post("/api/concierge-messages/delete-attachment", body);
}

// Delete a whole message (soft by default). Options: { hardDelete, deleteAttachments, reason }
export async function deleteMessage({ propertyId, userUid, messageId, hardDelete = false, deleteAttachments = true, reason }) {
  if (!propertyId) throw new Error("propertyId is required");
  if (!userUid) throw new Error("userUid is required");
  if (!messageId) throw new Error("messageId is required");
  const payload = { propertyId, userUid, messageId, hardDelete, deleteAttachments };
  if (reason) payload.reason = reason;
  console.debug("[conciergeApi] deleteMessage API call", {
    ts: new Date().toISOString(),
    ...payload
  });
  return apiClient.post("/api/concierge-messages/delete-message", payload);
}

export async function editConciergeMessage({ propertyId, userUid, messageId, content }) {
  if (!propertyId) throw new Error('propertyId is required');
  if (!userUid) throw new Error('userUid is required');
  if (!messageId) throw new Error('messageId is required');
  const final = (content ?? '').toString();
  return apiClient.post('/api/concierge-messages/edit-message', {
    propertyId,
    userUid,
    messageId,
    content: final,
  });
}

export async function reactToMessage({ propertyId, userUid, messageId, emoji, actor, action = 'toggle' }) {
  if (!propertyId) throw new Error('propertyId is required');
  if (!userUid) throw new Error('userUid is required');
  if (!messageId) throw new Error('messageId is required');
  if (!emoji) throw new Error('emoji is required');

  // Normalize action
  const act = (action === 'add' || action === 'remove' || action === 'toggle') ? action : 'toggle';

  // Normalize actor while preserving provided id/name fields
  let finalActor;
  if (actor && typeof actor === 'object') {
    // Pass through, only ensure role and string id
    const role = actor.role === 'guest' ? 'guest'
               : actor.role === 'staff' ? 'staff'
               : actor.role === 'bot' ? 'bot'
               : actor.role === 'system' ? 'system'
               : 'staff';
    finalActor = { ...actor, role };
    if (finalActor.id != null) finalActor.id = String(finalActor.id);
  } else if (actor === 'guest' || actor === 'hotel') {
    finalActor = { role: actor === 'guest' ? 'guest' : 'staff' };
  } else {
    // Derive a minimal staff actor; attempt to hint an id for stability
    let hintedId = null;
    try {
      if (typeof window !== 'undefined') {
        hintedId = window.__STAFF_ID__
          || (window.__STAFF__ && (window.__STAFF__._id || window.__STAFF__.id))
          || null;
      }
      if (!hintedId && typeof localStorage !== 'undefined') {
        hintedId = localStorage.getItem('staffId') || localStorage.getItem('userId') || null;
      }
    } catch {}
    finalActor = hintedId ? { role: 'staff', id: String(hintedId) } : { role: 'staff' };
  }
  
  const body = { propertyId, userUid, messageId, emoji, actor: finalActor, action: act };
  console.debug('[conciergeApi] reactToMessage', body);
  return apiClient.post('/api/concierge-messages/react', body);
}

// Convenience wrappers
export async function reactToggleHotel({ propertyId, userUid, messageId, emoji }) {
  let hintedId = null;
  try {
    if (typeof window !== 'undefined') {
      hintedId = window.__STAFF_ID__
        || (window.__STAFF__ && (window.__STAFF__._id || window.__STAFF__.id))
        || null;
    }
    if (!hintedId && typeof localStorage !== 'undefined') {
      hintedId = localStorage.getItem('staffId') || localStorage.getItem('userId') || null;
    }
  } catch {}
  const actor = hintedId ? { role: 'staff', id: String(hintedId) } : { role: 'staff' };
  return reactToMessage({ propertyId, userUid, messageId, emoji, actor, action: 'toggle' });
}

export async function reactToggleGuest({ propertyId, userUid, messageId, emoji }) {
  return reactToMessage({ propertyId, userUid, messageId, emoji, actor: { role: 'guest', id: userUid }, action: 'toggle' });
}

// ---------------- Quick Responses ----------------
export async function getQuickResponses({ propertyId }) {
  if (!propertyId) throw new Error('propertyId is required');
  const url = `/api/quick-responses?propertyId=${encodeURIComponent(propertyId)}`;
  const res = await apiClient.get(url);
  return Array.isArray(res?.items) ? res.items : [];
}

export async function saveQuickResponses({ propertyId, responses }) {
  if (!propertyId) throw new Error('propertyId is required');
  const payload = Array.isArray(responses) ? responses : [];
  const url = `/api/quick-responses/bulk?propertyId=${encodeURIComponent(propertyId)}`;
  return apiClient.put(url, payload);
}

// ---------------- Automations ----------------
export async function getAutomations({ propertyId }) {
  if (!propertyId) throw new Error('propertyId is required');
  const url = `/api/automations?propertyId=${encodeURIComponent(propertyId)}`;
  const res = await apiClient.get(url);
  return Array.isArray(res?.rules) ? res.rules : [];
}

export async function putAutomations({ propertyId, rules }) {
  if (!propertyId) throw new Error('propertyId is required');
  const url = `/api/automations/bulk?propertyId=${encodeURIComponent(propertyId)}`;
  const payload = Array.isArray(rules) ? rules : [];
  return apiClient.put(url, payload);
}

// Convenience alias
export async function saveAutomations({ propertyId, rules }) {
  return putAutomations({ propertyId, rules });
}
// Get a time-limited signed URL for a private attachment
export async function getSignedAttachmentUrl({ publicId, resourceType, filename, format, ttlSeconds = 300 }) {  if (!publicId) throw new Error('publicId is required');
  // 1) Prefer apiClient
  try {
    const res = await apiClient.post('/api/concierge-messages/signed-url', { publicId, resourceType, filename, format, ttlSeconds });
     if (res && res.url) return res.url;
    if (res && res.success && res.data && res.data.url) return res.data.url;
    throw new Error('No signed URL returned');
  } catch (e) {
    console.warn('[conciergeApi] getSignedAttachmentUrl apiClient.post failed, falling back to fetch', e);
    // 2) Fallback to absolute fetch using env base URL
    const envBase = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_URL)
      || (typeof process !== 'undefined' && process.env && (process.env.VITE_API_URL || process.env.REACT_APP_API_URL))
      || (apiClient && (apiClient.baseUrl || apiClient.defaults?.baseURL))
      || '';
    const base = String(envBase || '').replace(/\/$/, '');
    const url = base ? `${base}/api/concierge-messages/signed-url` : '/api/concierge-messages/signed-url';
    const r = await fetch(url, {
      method: 'POST',
      headers: (() => { const h = { 'Content-Type': 'application/json' }; const id = __resolveStaffIdHint(); if (id) h['x-staff-id'] = id; return h; })(),
      credentials: 'include',
      body: JSON.stringify({ publicId, resourceType, filename, format, ttlSeconds }),
    });
    if (!r.ok) throw new Error(`Signed URL endpoint failed (${r.status})`);
    const data = await r.json();
    if (data && data.url) return data.url;
    if (data && data.success && data.data && data.data.url) return data.data.url;
    throw new Error('No signed URL in response');
  }
}

// Convenience: sign directly from an attachment-like object { publicId, name, type, storedResourceType }
export async function getSignedAttachmentUrlFor(att, { ttlSeconds = 300, forceResourceType, format } = {}) {  if (!att || !att.publicId) throw new Error('att.publicId is required');
  const publicId = att.publicId;
  const filename = att.name || '';
  const overrideType = (forceResourceType && ['image','raw','video'].includes(forceResourceType)) ? forceResourceType : null;
  // Prefer explicit override, then storedResourceType, then inference
  let resourceType = overrideType
    || (att && typeof att.storedResourceType === 'string' && ['image','raw','video'].includes(att.storedResourceType)
      ? att.storedResourceType
      : undefined);
  if (!resourceType) {
    const s = String(filename || att.url || '').toLowerCase();
    const m = s.match(/\.([a-z0-9]{2,6})(?:$|\?)/);
    const ext = m ? m[1] : '';
    const doc = new Set(['pdf','zip','txt','csv','doc','docx','ppt','pptx','xls','xlsx']);
    if (doc.has(ext)) resourceType = 'raw';
    else if (/\.(mp4|mov|webm|mkv)$/i.test(s) || (att.type && String(att.type).startsWith('video/'))) resourceType = 'video';
    else if (att.type && String(att.type).startsWith('image/')) resourceType = 'image';
  }
  return getSignedAttachmentUrl({ publicId, resourceType, filename, format, ttlSeconds });
}
